"use client";

import { LogIn, UserCheck, CheckCircle, Bell, AlertTriangle, X, Pencil } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { useAppNotifications } from "@/shared/lib/hooks/use-app-notifications-unified";
import { isAxiosError } from "axios";

import { api } from "@/shared/lib/api";
import { handleSilentError } from "@/shared/api/error-handler";
import { tournamentApi } from "@/shared/api/tournament";
import {
  filterAccountsForTournamentRegion,
  normalizeTournamentRegionToPlatform,
} from "@/shared/lib/riot-region-compat";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/modals/dialog";
import type {
  Tournament,
  CustomRegistrationFieldDto,
  MyWaitlistPositionResponse,
} from "@/entities/tournament/model/types";
import type { RiotAccount } from "@/shared/api/user";
import { useTournamentStore } from "@/entities/tournament";
import {
  myRegistrationQueryKey,
  useMyRegistration,
} from "@/entities/tournament/api/use-my-registration";
import { useAuthStore } from "@/shared/model/auth-store";
import { useOrganizationRestriction } from "@/hooks/use-organization-restriction";
import { AuthService } from "@/shared/lib/auth-service";
import { TempTeamCreateDialog } from "@/features/tournament/temp-team-create-dialog";
import { TournamentRegistrationForm } from "@/widgets/tournament-registration/tournament-registration-form";
import { TournamentRegistrationEditForm } from "@/widgets/tournament-registration/tournament-registration-edit-form";
import { getTournamentStatusKey } from "@/entities/tournament/lib/tournament-lifecycle";
import { formatDateMsk, MSK_TIME_ZONE } from "@/shared/lib/msk-time";
import { trackEvent, AnalyticsEvents } from "@/shared/lib/analytics";
import { tournamentKeys } from "@/shared/api/query-keys";

interface TournamentActionsProps {
  tournament: Tournament;
}

interface MyRegistrationStatus {
  isRegistered: boolean;
  registrationId?: string | null;
  registrationStatus?: string | null;
  teamId?: string | null;
  isCheckedIn: boolean;
  checkedInAt?: string | null;
  draftEligible?: boolean;
  canCheckIn?: boolean;
  draftStartTime?: string | null;
  draftStartAt?: string | null;
  excludedFromDraftReason?: string | null;
  checkInDisabledReason?: string | null;
  // Содержимое заявки — приходит вместе со статусом, чтобы «Моя заявка»
  // открывалась уже заполненной без отдельного запроса.
  mainRole?: string | null;
  secondaryRole?: string | null;
  desiredRole?: string | null;
  playerNote?: string | null;
  canEditRegistration?: boolean;
}

function getApiErrorMessage(error: unknown, key: "message" | "error"): string | undefined {
  if (!isAxiosError<Record<string, unknown>>(error)) return undefined;
  const value = error.response?.data?.[key];
  return typeof value === "string" ? value : undefined;
}

// slug: не входит в текущий Tournament (entities/tournament) — fallback
// на случай старых закэшированных ответов с плоским полем.
function getTournamentSlug(tournament: Tournament): string | undefined {
  return (tournament as unknown as Record<string, unknown>)["slug"] as
    | string
    | undefined;
}

export const TournamentActions = ({ tournament }: TournamentActionsProps) => {
  const t = useTranslations("Tournaments");
  const isAuth = useAuthStore((s) => s.isAuthenticated);
  const router = useRouter();
  const notifications = useAppNotifications();
  const queryClient = useQueryClient();
  const refreshTournamentFromStore = useTournamentStore(
    (s) => s.fetchTournamentById,
  );
  const tournamentId = tournament.id ?? "";
  const tournamentType = tournament["type"] as string | null | undefined;
  // Arena Random — соло-регистрация (как Draft), Riot опционален при requireRiotAccount=false.
  const isArenaRandom =
    tournamentType === "Arena" &&
    (tournament as { arenaTeamMode?: string }).arenaTeamMode === "Random";
  const requiresRiot =
    (tournament as { requireRiotAccount?: boolean }).requireRiotAccount !== false;
  // Турниры с соло-регистрацией через registerSoloPlayer (Draft + Arena Random).
  const isSoloRegister =
    tournament.format === "Draft" || tournamentType === "Draft" || isArenaRandom;
  // Именно Draft (без Arena Random): капитанство и заметка для капитанов — только там.
  const isDraftFormat = tournament.format === "Draft" || tournamentType === "Draft";
  const statusKey = getTournamentStatusKey(tournament);
  const organizationId = tournament.organizationId ?? undefined;
  const { isRestricted } = useOrganizationRestriction(organizationId);
  /**
   * KAN-935: статус заявки берётся из ОБЩЕГО кэша React Query, а не сырым
   * `api.get` в локальный стейт. Раньше эту же ручку независимо спрашивали и
   * вкладки турнира — два запроса на каждое открытие страницы у каждого
   * залогиненного, потому что разные механизмы кэша друг о друге не знают.
   */
  const myRegistrationQuery = useMyRegistration(tournamentId, {
    enabled: isAuth && Boolean(tournamentId),
  });
  const status = (myRegistrationQuery.data ?? null) as MyRegistrationStatus | null;
  /** Обновить статус в общем кэше свежим ответом бэкенда. */
  const setStatus = (next: MyRegistrationStatus | null) => {
    if (!tournamentId) return;
    queryClient.setQueryData(myRegistrationQueryKey(tournamentId), next);
  };
  /**
   * KAN-368: `null` у status означает и «не загружали», и «загрузили, пусто».
   * Пока статус неизвестен, ветка «зарегистрирован» не срабатывает, и код
   * проваливается до кнопки «Зарегистрироваться» — то есть уже зарегистрированный
   * человек успевал увидеть предложение зарегистрироваться ещё раз, после чего оно
   * подменялось. Тот же класс, что мигание кнопки заявки в списке команд.
   */
  const statusKnown =
    !isAuth || !tournamentId || myRegistrationQuery.isFetched;
  const [registering, setRegistering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Диалог отмены — УПРАВЛЯЕМЫЙ намеренно. Он живёт внутри ветки «зарегистрирован»,
  // и если менять статус, пока диалог ещё закрывается, ветка размонтируется прямо
  // под ним: на её месте оказывается кнопка «Принять участие» в DialogTrigger, и
  // клик после закрытия попадал уже по ней — модалка регистрации открывалась сама.
  // Поэтому сначала закрываем диалог, и только потом трогаем статус.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [postRegisterOpen, setPostRegisterOpen] = useState(false);
  const [postRegisterDraftStartIso, setPostRegisterDraftStartIso] = useState<
    string | null
  >(null);
  const [reminderSubscribed, setReminderSubscribed] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState<MyWaitlistPositionResponse | null>(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  // Поля заявки (роли, капитанство, заметка) живут в TournamentRegistrationForm и
  // TournamentRegistrationEditForm — здесь их состояния больше нет. Раньше оно
  // дублировалось трижды и расходилось между формами.
  const [myRegOpen, setMyRegOpen] = useState(false);
  const [riotAccountId, setRiotAccountId] = useState("");
  const [riotAccounts, setRiotAccounts] = useState<RiotAccount[]>([]);
  const [regionBlockedOpen, setRegionBlockedOpen] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const customRegistrationFields = (tournament as { customRegistrationFields?: CustomRegistrationFieldDto[] }).customRegistrationFields ?? [];

  // Диалог регистрации нужен только если есть что заполнять: выбор Riot-аккаунта,
  // роль (не Arena Random), кастом-вопросы или предупреждение об ограничении.
  // Иначе (соло-Random без Riot) — регистрируем в ОДИН КЛИК, без пустой модалки.
  const needsRegisterDialog =
    isRestricted ||
    requiresRiot ||
    !isArenaRandom ||
    customRegistrationFields.length > 0;

  const refetchMyRegistration = myRegistrationQuery.refetch;
  const fetchStatus = useCallback(async () => {
    if (!isAuth || !tournamentId) return;
    const result = await refetchMyRegistration();
    if (result.error) {
      handleSilentError(result.error, {
        component: "tournament-actions",
        action: "fetchMyRegistration",
      });
    }
  }, [isAuth, tournamentId, refetchMyRegistration]);

  // После реги/отмены обновляем все связанные данные БЕЗ перезагрузки страницы:
  // - React Query гриды и my-registration (префиксы ["tournaments", id] и ["tournament", id]);
  // - сам объект турнира (счётчики участников / capabilities) — он живёт в Zustand-сторе;
  // - событие для грида участников, который слушает window-эвент, а не React Query.
  const refreshAfterRegistrationChange = () => {
    if (!tournamentId) return;
    void queryClient.invalidateQueries({ queryKey: tournamentKeys.detail(tournamentId) });
    void refreshTournamentFromStore(tournamentId);
    try {
      window.dispatchEvent(
        new CustomEvent("tournament:participants:refresh", {
          detail: { tournamentId },
        }),
      );
    } catch {
      // ignore
    }
  };

  const fetchRiotAccounts = useCallback(async () => {
    if (!isAuth) return;
    try {
      const accounts = await AuthService.getRiotAccounts();
      setRiotAccounts(accounts || []);
    } catch (error) {
      handleSilentError(error, {
        component: "tournament-actions",
        action: "fetchRiotAccounts",
      });
    }
  }, [isAuth]);

  /**
   * KAN-935: список Riot-аккаунтов раньше грузился при КАЖДОМ открытии страницы
   * турнира, хотя нужен только чтобы решить, не блокировать ли регистрацию по
   * региону. Отложен до момента, когда регистрация вообще возможна и человек
   * ещё не зарегистрирован — на завершённом/идущем турнире и у уже
   * зарегистрированного запрос не уходит вовсе.
   *
   * Откладывать до клика по «Участвовать» нельзя: `shouldBlockByRegion`
   * читается прямо в обработчике клика, и первый клик проскочил бы мимо
   * проверки региона (см. Edge cases кана).
   */
  const mayNeedRiotAccounts =
    isAuth &&
    Boolean(tournamentId) &&
    statusKnown &&
    !status?.isRegistered &&
    (tournament.capabilities?.canRegister === true ||
      statusKey === "registrationopen");

  useEffect(() => {
    if (!mayNeedRiotAccounts) return;
    fetchRiotAccounts();
  }, [tournamentId, isAuth, mayNeedRiotAccounts, fetchRiotAccounts]);

  const tournamentRegionPlatform = useMemo(
    () => normalizeTournamentRegionToPlatform(tournament.region),
    [tournament.region],
  );
  const eligibleRiotAccounts = useMemo(
    () => filterAccountsForTournamentRegion(riotAccounts, tournament.region),
    [riotAccounts, tournament.region],
  );
  const hasAnyRiotAccounts = riotAccounts.length > 0;
  const shouldBlockByRegion =
    requiresRiot &&
    Boolean(tournamentRegionPlatform) &&
    eligibleRiotAccounts.length === 0;

  useEffect(() => {
    if (!eligibleRiotAccounts.length) {
      setRiotAccountId("");
      return;
    }
    if (riotAccountId && eligibleRiotAccounts.some((a) => a?.id === riotAccountId))
      return;
    // isPrimary: легаси-имя поля вне текущей RiotAccountSchema (актуальное — isMain) —
    // fallback на случай старых закэшированных ответов.
    const primary =
      eligibleRiotAccounts.find(
        (a) => a?.isMain || (a as unknown as Record<string, unknown>)?.["isPrimary"],
      ) ?? eligibleRiotAccounts[0];
    setRiotAccountId(primary?.id ?? "");
  }, [eligibleRiotAccounts, riotAccountId]);

  const statusIsRegistered = status?.isRegistered;
  const fetchReminder = useCallback(async () => {
    if (!isAuth || !tournamentId) return;
    if (statusIsRegistered) return;
    if (statusKey !== "upcoming") return;

    try {
      const { data } = await api.get<{ subscribed?: boolean }>(
        `/tournaments/${tournamentId}/reminders/registration-open/me`,
      );
      setReminderSubscribed(Boolean(data?.subscribed));
    } catch {
      setReminderSubscribed(false);
    }
  }, [isAuth, tournamentId, statusIsRegistered, statusKey]);

  useEffect(() => {
    fetchReminder();
  }, [tournamentId, isAuth, statusKey, status?.isRegistered, fetchReminder]);

  const fetchWaitlistPosition = useCallback(async () => {
    if (!isAuth || !tournamentId) return;
    try {
      const { data } = await api.get<MyWaitlistPositionResponse>(`/tournaments/${tournamentId}/waitlist/me`);
      setWaitlistPosition(data ?? null);
    } catch {
      setWaitlistPosition(null);
    }
  }, [isAuth, tournamentId]);

  // Драфт заполняется по игрокам (playerLimit = MaxPlayers), а не по командам.
  // Без лимита (playerLimit пуст) — турнир никогда не «полон».
  const isFull =
    tournament.type === "Draft"
      ? tournament.playerLimit != null &&
        (tournament.registeredPlayersCount ?? 0) >= tournament.playerLimit
      : (tournament.currentTeams ?? 0) >= (tournament.maxTeams ?? tournament.maxPlayers ?? 0);
  const showWaitlist =
    tournament.waitlistEnabled &&
    isFull &&
    statusKey === "registrationopen" &&
    !status?.isRegistered;

  useEffect(() => {
    if (showWaitlist) fetchWaitlistPosition();
    else setWaitlistPosition(null);
  }, [showWaitlist, tournamentId, isAuth, fetchWaitlistPosition]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent)?.detail as
        | { tournamentId?: string }
        | undefined;
      if (!detail?.tournamentId) return;
      if (detail.tournamentId !== tournamentId) return;
      fetchStatus();
    };

    window.addEventListener("tournament:participants:refresh", handler);
    return () => {
      window.removeEventListener("tournament:participants:refresh", handler);
    };
  }, [tournamentId, isAuth, fetchStatus]);

  const handleCancelRegistration = async () => {
    if (!tournamentId || !status?.registrationId) return;
    try {
      setCancelling(true);
      // Закрываем диалог ДО смены статуса — иначе ветка «зарегистрирован»
      // размонтируется во время его закрытия (см. комментарий у cancelDialogOpen).
      setCancelDialogOpen(false);
      await api.post(
        `/tournaments/${tournamentId}/registrations/${status.registrationId}/cancel`,
        {},
      );
      notifications.success(t("actions.cancelRegistrationSuccess"));
      // Без setStatus(null): состояние обновляем ОДНИМ шагом по свежему ответу.
      // Промежуточный null дополнительно дёргал вёрстку (ветка «не зарегистрирован»
      // на мгновение подменяла кнопки) — ровно тот момент, когда и проскакивал клик.
      await fetchStatus();
      refreshAfterRegistrationChange();
    } catch (error) {
      notifications.error(
        (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data
          ?.error ??
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          t("actions.cancelRegistrationError"),
      );
    } finally {
      setCancelling(false);
    }
  };

  /**
   * Открыть «Мою заявку». Предзаполнение живёт в самой форме: она снимает значения
   * при монтировании, поэтому фоновый рефетч статуса не затирает уже введённое.
   */
  const openMyRegistration = () => {
    setMyRegOpen(true);
  };

  /**
   * Хвост успешной регистрации: сама отправка живёт в TournamentRegistrationForm
   * (через useTournamentRegistration), а здесь — то, что знает только страница турнира:
   * аналитика, модалка про check-in и обновление трёх источников состояния.
   */
  const handleRegistrationSuccess = async () => {
    setRegisterDialogOpen(false);

    // Конверсия для аналитики: даёт воронку «визит → регистрация» в Umami.
    // Только факт события + тип турнира, без персональных данных.
    trackEvent(AnalyticsEvents.TournamentRegistered, {
      tournament: tournament.slug || tournamentId,
      type: String(tournamentType ?? tournament.format ?? "unknown"),
    });

    // KAN-935: свежий статус берём тем же общим запросом, а не отдельным
    // `api.get` — иначе кэш вкладок остался бы со старым значением.
    const { data } = await myRegistrationQuery.refetch();
    // Пост-рег модалка про check-in/драфт-комнату актуальна ТОЛЬКО для драфтов.
    // Для Arena Random (тоже соло-рега) драфта и вкладки «Драфт» нет — не показываем.
    if (isDraftFormat) {
      const iso = (data?.["draftStartAt"] ?? data?.["draftStartTime"]) as
        | string
        | null
        | undefined;
      setPostRegisterDraftStartIso(iso ?? null);
      setPostRegisterOpen(true);
    }

    refreshAfterRegistrationChange();
  };

  /**
   * Регистрация «в один клик» — единственный путь, где модалка не открывается.
   * Срабатывает только когда заполнять нечего: Arena Random без Riot-аккаунта,
   * без кастомных полей и без ограничения организации (см. needsRegisterDialog).
   * Ролей, капитанства и заметки здесь нет по построению — они только у драфта,
   * а драфт всегда открывает форму.
   *
   * Во всех остальных случаях регистрация идёт через TournamentRegistrationForm.
   */
  const handleRegister = async () => {
    if (!tournamentId) return;

    try {
      setRegistering(true);
      const result = await tournamentApi.registerSoloPlayer({
        tournamentId,
        riotAccountId,
        desiredRole: "Participant",
      });
      // Бэкенд может вернуть 200 с success=false (напр. регистрация не создалась) —
      // не показываем ложный «успех», а сообщаем об ошибке.
      if (result && result.success === false) {
        notifications.error(t("actions.errorRegistration"));
        return;
      }
      notifications.success(t("actions.successRegistered"));
      await handleRegistrationSuccess();
    } catch (error) {
      notifications.error(
        getApiErrorMessage(error, "message") || t("actions.errorRegistration"),
      );
    } finally {
      setRegistering(false);
    }
  };

  const handleCheckIn = async () => {
    if (!tournamentId) return;
    try {
      setCheckInLoading(true);
      await api.post(`/tournaments/${tournamentId}/check-in`);
      notifications.success(t("actions.successCheckIn"));
      fetchStatus();
      // KAN-41: check-in меняет my-registration — инвалидируем React Query
      // (ключ ["tournaments", id, "my-registration"] покрывается префиксом),
      // стор и грид участников, как после реги/отмены. Иначе статус в соседних
      // компонентах не обновлялся без полного релоада.
      refreshAfterRegistrationChange();
    } catch (error) {
      notifications.error(getApiErrorMessage(error, "error") || t("actions.errorCheckIn"));
    } finally {
      setCheckInLoading(false);
    }
  };

  const handleRemindRegistrationOpen = async () => {
    if (!tournamentId) return;
    try {
      setReminderLoading(true);
      await api.post(`/tournaments/${tournamentId}/reminders/registration-open`);
      setReminderSubscribed(true);
      notifications.success(t("actions.successReminder"));
    } catch (error) {
      notifications.error(
        getApiErrorMessage(error, "error") || t("actions.errorReminder"),
      );
    } finally {
      setReminderLoading(false);
    }
  };

  // Don't show registration actions for finished tournaments
  if (statusKey === "completed" || statusKey === "cancelled") {
    return null;
  }

  if (statusKey === "draft" && tournament.capabilities?.canEditTournament) {
    const slug = getTournamentSlug(tournament);
    const href = slug ? `/tournaments/${slug}` : tournamentId ? `/tournaments/${tournamentId}` : null;
    return (
      <Button
        variant="secondary"
        onClick={() => href && router.push(`${href}?tab=management`)}
        disabled={!href}
      >
        {t("actions.openManagement")}
      </Button>
    );
  }

  // Show draft badge for non-admins
  if (statusKey === "draft") {
    return (
      <Button variant="outline" disabled>
        {t("actions.notPublished")}
      </Button>
    );
  }

  if (!isAuth) {
    return (
      <Button variant="outline" onClick={() => router.push("/login")}>
        <LogIn className="mr-2 h-4 w-4" />
        {t("actions.loginToParticipate")}
      </Button>
    );
  }

  /**
   * KAN-368: пока ответ о своей регистрации не получен, действий не показываем.
   * Иначе зарегистрированный пользователь видит вспышку «Зарегистрироваться» —
   * та же болезнь, что и мигание кнопки заявки в списке команд: «ещё не знаем»
   * трактовалось как «нет».
   *
   * Неавторизованному ждать нечего — ему статус не запрашивают.
   */
  if (isAuth && !statusKnown) {
    return (
      <div className="h-9 w-40 animate-pulse rounded-md bg-muted" aria-hidden />
    );
  }

  // If user is registered
  if (status?.isRegistered) {
    // Само-отмена регистрации: пока регистрация открыта (бэкенд гейтит
    // canCancelRegistration). Кнопка добавляется ко ВСЕМ состояниям «зарегистрирован».
    const canCancel =
      Boolean(tournament.capabilities?.canCancelRegistration) &&
      Boolean(status.registrationId);
    const cancelButton = canCancel ? (
      <AlertDialog
        open={cancelDialogOpen}
        onOpenChange={(v) => {
          // Во время запроса диалог не трогаем: закроем его сами в обработчике.
          if (cancelling) return;
          setCancelDialogOpen(v);
        }}
      >
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={cancelling}
            className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="mr-1.5 h-4 w-4" />
            {cancelling
              ? t("actions.cancellingRegistration")
              : t("actions.cancelRegistration")}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("actions.cancelRegistration")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("actions.cancelRegistrationConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>
              {t("actions.cancelRegistrationKeep")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelRegistration}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("actions.cancelRegistration")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    ) : null;

    const statusButton = (() => {
      const isDraft = tournament.format === "Draft" || tournamentType === "Draft";

      if (!isDraft) {
        return (
          <Button variant="secondary" disabled>
            {t("actions.registered")}
          </Button>
        );
      }

    if ((status.registrationStatus ?? "").toLowerCase() === "pending") {
      return (
        <Button variant="secondary" disabled>
          {t("actions.pendingConfirmation")}
        </Button>
      );
    }

    // Чек-ин у турнира выключен — говорим просто «зарегистрирован». Раньше эта ветка
    // проваливалась в фолбэк ниже и участник видел «ожидается check-in» у турнира,
    // где чек-ина нет вовсе.
    if (status.checkInDisabledReason === "check_in_disabled") {
      return (
        <Button variant="secondary" disabled>
          {t("actions.registered")}
        </Button>
      );
    }

    if (status.excludedFromDraftReason === "blacklisted") {
      return (
        <Button variant="secondary" disabled>
          {t("actions.blacklisted")}
        </Button>
      );
    }

    if (status.isCheckedIn) {
      return (
        <Button
          variant="outline"
          className="bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20 hover:text-green-700 cursor-default"
        >
          <CheckCircle className="mr-2 h-4 w-4" />
          {t("actions.readyCheckIn")}
        </Button>
      );
    }

    if (status.canCheckIn) {
      return (
        <Button
          onClick={handleCheckIn}
          disabled={checkInLoading}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <UserCheck className="mr-2 h-4 w-4" />
          {checkInLoading
            ? t("actions.confirmingCheckIn")
            : t("actions.confirmCheckIn")}
        </Button>
      );
    }

    /**
     * KAN-366: «Не задано время старта драфта» — сообщение ОРГАНИЗАТОРУ, а показывали
     * его участнику: он видел вечно неактивную кнопку чек-ина и подпись про поле,
     * которое не может заполнить. Кнопка, которая никогда не станет доступной для
     * того, кто на неё смотрит, — не подсказка, а шум; убираем её целиком.
     *
     * Остальные причины временные («рано», «поздно», «ждём»), и по ним неактивная
     * кнопка честно говорит, чего ждать.
     */
    if (status.checkInDisabledReason === "no_draft_start") {
      return null;
    }

    return (
      <Button variant="secondary" disabled>
        {status.checkInDisabledReason === "too_early"
          ? t("actions.checkInTooEarly")
          : status.checkInDisabledReason === "too_late"
            ? t("actions.checkInTooLate")
            : t("actions.checkInWaiting")}
      </Button>
    );
    })();

    // «Моя заявка» — правка роли/капитанства/заметки. Показываем только пока
    // бэкенд разрешает правку (до старта драфта): кнопка, которая всегда упрётся
    // в отказ, хуже, чем её отсутствие.
    const myRegistrationButton = status.canEditRegistration ? (
      <Button variant="outline" size="sm" onClick={openMyRegistration}>
        <Pencil className="mr-1.5 h-4 w-4" />
        {t("actions.myRegistration")}
      </Button>
    ) : null;

    const statusLabel =
      (status.registrationStatus ?? "").toLowerCase() === "approved"
        ? t("actions.statusApproved")
        : (status.registrationStatus ?? "").toLowerCase() === "rejected"
          ? t("actions.statusRejected")
          : t("actions.statusPending");

    return (
      <>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          {statusButton}
          {myRegistrationButton}
          {cancelButton}
        </div>

        <Dialog open={myRegOpen} onOpenChange={setMyRegOpen}>
          {/* max-w-lg, а не max-w-md: 448px были уже, чем мобильный предел в 480px —
              подсказки в форме ломались на три строки. Диалоги-сообщения ниже
              остаются узкими, им ширина не нужна. */}
          <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[480px] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("actions.myRegistration")}</DialogTitle>
              <DialogDescription>{t("actions.myRegistrationDesc")}</DialogDescription>
            </DialogHeader>

            {/* Та же форма полей, что и при регистрации: раньше здесь была третья
                копия со своим набором (без дополнительной роли), и правка заявки
                молча стирала её — бэкенд присваивает SecondaryRole безусловно. */}
            <TournamentRegistrationEditForm
              tournamentId={tournamentId}
              registration={{
                mainRole: status?.mainRole,
                secondaryRole: status?.secondaryRole,
                desiredRole: status?.desiredRole,
                playerNote: status?.playerNote,
              }}
              isDraft={isDraftFormat}
              statusSlot={
                <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                  <span className="text-sm text-muted-foreground">
                    {t("actions.registrationStatusLabel")}
                  </span>
                  <span className="text-sm font-semibold">{statusLabel}</span>
                </div>
              }
              onSaved={(updated) => {
                // Бэкенд возвращает свежий статус — берём его, не гадая на клиенте.
                setStatus(updated as MyRegistrationStatus);
                setMyRegOpen(false);
                notifications.success(t("actions.myRegistrationSaved"));
                refreshAfterRegistrationChange();
              }}
              onCancel={() => setMyRegOpen(false)}
            />

          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Not registered yet
  // Полный турнир при открытой реге: не даём кликнуть «Участвовать» (иначе бэк
  // вернёт 409 «Турнир заполнен»). Вместо этого — вейтлист (если включён) или «Мест нет».
  const isFullOpen = isFull && statusKey === "registrationopen";
  const canRegister =
    !isRestricted &&
    !isFullOpen &&
    (tournament.capabilities?.canRegister ?? statusKey === "registrationopen");
  
  if (isRestricted) {
    return (
      <div className="space-y-3">
        <div className="p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-md flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
              {t("actions.restrictedTitle")}
            </p>
            <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
              {t("actions.restrictedDesc")}
            </p>
          </div>
        </div>
        <Button size="lg" variant="outline" disabled>
          {t("actions.participate")}
        </Button>
      </div>
    );
  }
  
  if (!canRegister) {
    if (showWaitlist) {
      if (waitlistPosition) {
        return (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" disabled>
              {t("actions.waitlistPosition", { position: waitlistPosition.position })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  setWaitlistLoading(true);
                  await api.delete(`/tournaments/${tournamentId}/waitlist/leave`);
                  setWaitlistPosition(null);
                  // KAN-147 #3: waitlist меняет состояние участия — рефреш 3 источников.
                  refreshAfterRegistrationChange();
                  notifications.success(t("actions.waitlistLeft"));
                } catch (e: unknown) {
                  const err = e as { response?: { data?: { message?: string } } };
                  notifications.error(err.response?.data?.message ?? t("actions.errorLeaveWaitlist"));
                } finally {
                  setWaitlistLoading(false);
                }
              }}
              disabled={waitlistLoading}
            >
              {t("actions.leaveWaitlist")}
            </Button>
          </div>
        );
      }
      return (
        <Button
          variant="outline"
          onClick={async () => {
            try {
              setWaitlistLoading(true);
              const body = tournament.format === "Classic" || tournamentType === "Classic"
                ? { tournamentId: tournamentId, teamId: (tournament as { myTeamId?: string }).myTeamId ?? undefined }
                : { tournamentId: tournamentId };
              await api.post(`/tournaments/${tournamentId}/waitlist/join`, body);
              notifications.success(t("actions.waitlistJoined"));
              fetchWaitlistPosition();
              // KAN-147 #3: waitlist меняет состояние участия — рефреш 3 источников.
              refreshAfterRegistrationChange();
            } catch (e: unknown) {
              const err = e as { response?: { data?: { message?: string } } };
              notifications.error(err.response?.data?.message ?? t("actions.errorJoinWaitlist"));
            } finally {
              setWaitlistLoading(false);
            }
          }}
          disabled={waitlistLoading}
        >
          {t("actions.joinWaitlist")}
        </Button>
      );
    }

    if (isFullOpen) {
      return (
        <Button variant="outline" disabled>
          {t("actions.full")}
        </Button>
      );
    }

    if (statusKey === "upcoming") {
      if (reminderSubscribed) {
        return (
          <Button variant="secondary" disabled>
            <Bell className="mr-2 h-4 w-4" />
            {t("actions.reminderEnabled")}
          </Button>
        );
      }

      return (
        <Button
          variant="secondary"
          onClick={handleRemindRegistrationOpen}
          disabled={reminderLoading}
        >
          <Bell className="mr-2 h-4 w-4" />
          {reminderLoading ? t("actions.reminding") : t("actions.remindMe")}
        </Button>
      );
    }

    /**
     * KAN-367: когда турнир уже идёт или закончился, регистрация не «закрыта до
     * поры» — она осталась в прошлом безвозвратно. Выключенная кнопка про неё
     * занимала место рядом с живыми действиями и сообщала то, что и так видно по
     * статусу турнира.
     *
     * Правило: действие, невозможное в текущей фазе НАВСЕГДА, убираем с экрана.
     * Выключенная кнопка уместна, только когда действие ещё может стать доступным
     * и рядом сказано, чего ждать — как «Открыть лобби драфта» в KAN-358.
     */
    const registrationIsHistory =
      statusKey === "inprogress" ||
      statusKey === "ongoing" ||
      statusKey === "draftinprogress" ||
      statusKey === "awaitingbracket" ||
      statusKey === "completed" ||
      statusKey === "cancelled";
    if (registrationIsHistory) return null;

    return (
      <Button variant="outline" disabled>
        {t("actions.registrationClosed")}
      </Button>
    );
  }

  if (!isSoloRegister) {
    const requiredTeamSize = tournament.maxTeamSize ?? 5;
    const isClassic = tournament.format === "Classic";
    const isTempTeamTournament =
      isClassic && requiredTeamSize > 1 && requiredTeamSize < 5;

    if (isTempTeamTournament) {
      return (
        <>
          <TempTeamCreateDialog
            tournamentId={tournamentId}
            goToRegisterAfterCreate
            trigger={
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
                onClick={(e) => {
                  if (shouldBlockByRegion) {
                    e.preventDefault();
                    e.stopPropagation();
                    setRegionBlockedOpen(true);
                  }
                }}
              >
                {t("actions.participate")}
              </Button>
            }
          />
        </>
      );
    }

    return (
      <>
        <Button
          size="lg"
          className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
          onClick={() => {
            if (shouldBlockByRegion) {
              setRegionBlockedOpen(true);
              return;
            }
            router.push(`/tournaments/${tournamentId}/register`);
          }}
        >
          {t("actions.participate")}
        </Button>
      </>
    );
  }

  return (
    <>
      <Dialog
        open={registerDialogOpen}
        onOpenChange={(v) => {
          if (shouldBlockByRegion) {
            setRegionBlockedOpen(true);
            return;
          }
          setRegisterDialogOpen(Boolean(v));
        }}
      >
        <DialogTrigger asChild>
          <Button
            size="lg"
            disabled={registering}
            className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
            onClick={(e) => {
              if (shouldBlockByRegion) {
                e.preventDefault();
                setRegionBlockedOpen(true);
                return;
              }
              // Один клик: заполнять нечего → регистрируем сразу, без пустой модалки.
              if (!needsRegisterDialog) {
                e.preventDefault();
                if (!registering) void handleRegister();
              }
            }}
          >
            {registering ? t("actions.registering") : t("actions.participate")}
          </Button>
        </DialogTrigger>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[480px] sm:max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto" preventCloseOnOutside>
          <DialogHeader>
            <DialogTitle>{t("actions.registerTitle")}</DialogTitle>
            <DialogDescription>
              {t("actions.registerDesc", { name: tournament.name })}
            </DialogDescription>
          </DialogHeader>

        {isRestricted && (
          <div className="p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-md flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
            <p className="text-sm text-orange-800 dark:text-orange-200">
              {t("actions.restrictedDesc")}
            </p>
          </div>
        )}

        {/* Единая форма заявки — та же, что на странице регистрации и в правке заявки.
            Раньше здесь была урезанная копия без дополнительной роли, из-за чего набор
            полей зависел от того, каким путём человек регистрировался. */}
        <TournamentRegistrationForm
          tournament={tournament}
          showFormatCard={false}
          onSuccess={handleRegistrationSuccess}
        />

        </DialogContent>
      </Dialog>

      <Dialog open={regionBlockedOpen} onOpenChange={setRegionBlockedOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[480px] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("actions.regionBlockedTitle")}</DialogTitle>
            <DialogDescription>
              {!hasAnyRiotAccounts ? (
                <>
                  {t("actions.regionBlockedNoAccounts")}
                  {tournamentRegionPlatform ? (
                    <>
                      {" "}
                      {t("actions.regionBlockedNeedRegion", { region: tournamentRegionPlatform })}
                    </>
                  ) : null}
                </>
              ) : tournamentRegionPlatform ? (
                <>
                  {t("actions.regionBlockedForRegion", { region: tournamentRegionPlatform })}
                </>
              ) : (
                <>{t("actions.regionBlockedNeedTournamentRegion")}</>
              )}{" "}
              {t("actions.addAccount")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={() => setRegionBlockedOpen(false)} className="w-full sm:w-auto">
              {t("actions.ok")}
            </Button>
            <Button
              onClick={() => {
                setRegionBlockedOpen(false);
                router.push("/profile?tab=accounts");
              }}
              className="w-full sm:w-auto"
            >
              {t("actions.openProfile")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={postRegisterOpen} onOpenChange={setPostRegisterOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[480px] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("actions.postRegisterTitle")}</DialogTitle>
            <DialogDescription>
              {t("actions.postRegisterDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            {postRegisterDraftStartIso ? (
              <>
                <div>
                  {t("actions.postRegisterDraftStart", { timezone: MSK_TIME_ZONE })}{" "}
                  <span className="font-medium text-foreground">
                    {formatDateMsk(postRegisterDraftStartIso)}
                  </span>
                </div>
                <div>
                  {t("actions.postRegisterCheckInOpens")}
                </div>
              </>
            ) : (
              <div>
                {t("actions.postRegisterCheckInOpensNoDate")}
              </div>
            )}
            <div>
              {t("actions.postRegisterDraftTab")}
            </div>
          </div>
          <div className="pt-2">
            <Button
              className="w-full"
              onClick={() => {
                const slug = getTournamentSlug(tournament);
                const href = slug
                  ? `/tournaments/${slug}`
                  : tournamentId
                    ? `/tournaments/${tournamentId}`
                    : null;
                if (href) router.push(`${href}?tab=draft`);
                setPostRegisterOpen(false);
              }}
            >
              {t("actions.openDraftTab")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
